/**
 * §A.7 · Las peticiones de retirada del directorio.
 *
 * ## Es una bandeja con reloj, y eso es todo lo que es
 *
 * `brainstorming.md` §8.2: solo listamos información ya publicada por el propio club,
 * **y retirada inmediata a la primera petición**. El contrato dice por qué existe
 * esta pantalla: «sin una bandeja con reloj, "inmediata" se convierte en "cuando
 * alguien mire el correo"».
 *
 * ## `still_listed` es una alarma, no un estado
 *
 * «La ficha se esconde al recibir la petición: si esto viene en `true`, la retirada
 * inmediata que prometimos no ocurrió.» Así que va arriba, en rojo y contado, antes
 * de cualquier tabla. Es la única cosa de esta sección que puede ser un problema
 * legal de verdad.
 *
 * ## Y `reopened` es el caso que nadie adivina
 *
 * Alguien pidió la baja, se atendió, y la ficha volvió a aparecer —una reimportación
 * del censo, por ejemplo—. Tiene nombre propio en la pantalla porque es justo lo que
 * hay que ver.
 *
 * ## Aquí solo se mira
 *
 * §A.7 da **una** ruta: listar. Atender una petición es responder a una persona, y
 * eso pasa por correo. Se dice, porque una lista sin botones invita a buscar el botón.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, fechaYHora } from '@/shared/ui/formato';
import {
  esperaEnPalabras,
  etiquetaDeBaja,
  type PeticionDeBaja,
} from '@/backoffice/tiposDeDirectorio';

export function BajasDelDirectorio({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const bajas = useQuery({
    queryKey: ['admin', 'directorio', 'bajas'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.directory[
        'removal-requests'
      ].get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PeticionDeBaja[];
    },
  });

  if (bajas.isPending) return <Cargando filas={4} etiqueta="Cargando" />;

  if (bajas.isError || !bajas.data) {
    return (
      <PanelError
        problema={aProblema(bajas.error)}
        reintentar={() => void bajas.refetch()}
      />
    );
  }

  const siguenListadas = bajas.data.filter((b) => b.still_listed);

  return (
    <section>
      <h1>Peticiones de baja</h1>

      <p className="tenue">
        Clubes que piden salir del directorio. Prometimos{' '}
        <strong>retirada inmediata a la primera petición</strong>, así que esta
        lista existe para que «inmediata» no sea «cuando alguien mire el
        correo».
      </p>

      {/* La única cosa de aquí que puede ser un problema legal de verdad. */}
      {siguenListadas.length > 0 && (
        <div className="aviso aviso-grave" role="alert">
          <p>
            <strong>
              {cuenta(siguenListadas.length, 'ficha', 'fichas')} sigue
              apareciendo en el directorio después de pedir la baja.
            </strong>{' '}
            La ficha se esconde al recibir la petición, así que esto no debería
            poder pasar: hay que retirarla a mano ahora.
          </p>
          <p className="tenue">
            {siguenListadas.map((b) => b.academy_name).join(', ')}.
          </p>
        </div>
      )}

      {bajas.data.length === 0 ? (
        <Vacio
          titulo="Ninguna petición"
          explicacion="Nadie ha pedido salir del directorio. Es lo esperable: la ficha lista lo que el club ya publica por su cuenta."
        />
      ) : (
        <ul className="papeles">
          {bajas.data.map((b) => (
            <li
              key={b.id}
              className={b.still_listed ? 'fila-marcada' : undefined}
            >
              <div className="aviso-cabeza">
                <strong>{b.academy_name}</strong>
                <span className="pastilla pastilla-left">
                  {etiquetaDeBaja(b.status)}
                </span>
                {b.handled_at === null && (
                  <span className="pastilla pastilla-left">
                    Esperando {esperaEnPalabras(b.hours_waiting)}
                  </span>
                )}
                {b.still_listed && (
                  <span className="pastilla pastilla-mal">Sigue listada</span>
                )}
              </div>

              <p className="tenue">
                {[b.requester_name, b.requester_email, b.requester_role]
                  .filter(Boolean)
                  .join(' · ') || 'Sin datos de quien lo pide'}
              </p>

              {b.reason && <p>{b.reason}</p>}
              {b.note && <p className="tenue">Nota interna: {b.note}</p>}

              <div className="aviso-pie">
                <span className="tenue">
                  Recibida el {fechaYHora(b.received_at)}
                  {b.handled_at && ` · atendida el ${fechaYHora(b.handled_at)}`}
                </span>
                <button
                  type="button"
                  className="btn-plano"
                  onClick={() => alAbrirClub(b.academy_id)}
                >
                  Ver el club
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="tenue">
        {/* Por qué no hay botones: contestar a una persona se hace por correo. */}
        Desde aquí solo se mira. Atender una petición es contestarle a alguien,
        y eso se hace por correo; lo que esta pantalla tiene que garantizar es
        que no se queda sin ver.
      </p>
    </section>
  );
}
